import { getPerfectToday } from '@/lib/perfect-today';
import type { InterruptedSession, Word } from '@/types';

export interface SessionInit {
  perfectWordIds: Set<string>;
  deck: Word[];
  deckIndex: number;
  roundCount: number;
  freeReplay: boolean;
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function fullReplay(words: Word[]): SessionInit {
  return {
    perfectWordIds: new Set(),
    deck: shuffle([...words]),
    deckIndex: 0,
    roundCount: 1,
    freeReplay: true,
  };
}

/**
 * セッション開始 / 中断再開時のデッキ・完璧状態を決定する。
 * 中断データ + 今日の完璧キャッシュを合わせて除外し、
 * プレイ可能な単語がなければ自動的にもう一周モードにする。
 */
export function createSessionInit(
  words: Word[],
  initialState: InterruptedSession | undefined,
  ignorePerfectToday: boolean,
  dayStartHour: number,
): SessionInit {
  if (words.length === 0) {
    return {
      perfectWordIds: new Set(),
      deck: [],
      deckIndex: 0,
      roundCount: 1,
      freeReplay: false,
    };
  }

  if (ignorePerfectToday || initialState?.ignorePerfectToday) {
    return fullReplay(words);
  }

  const todayPerfect = getPerfectToday(dayStartHour);

  if (initialState) {
    const excluded = new Set([...initialState.perfectWordIds, ...todayPerfect]);
    const playableAll = words.filter((w) => !excluded.has(w.id));

    const playableFromSaved = initialState.deckWordIds
      .map((id) => words.find((w) => w.id === id))
      .filter((w): w is Word => !!w && !excluded.has(w.id));

    const deck =
      playableFromSaved.length > 0 ? playableFromSaved : shuffle(playableAll);

    if (deck.length === 0) {
      return fullReplay(words);
    }

    return {
      perfectWordIds: new Set(initialState.perfectWordIds),
      deck,
      deckIndex: Math.min(initialState.currentDeckIndex, deck.length - 1),
      roundCount: initialState.roundCount,
      freeReplay: false,
    };
  }

  const cachedSet = new Set(todayPerfect);
  const deck = shuffle(words.filter((w) => !cachedSet.has(w.id)));

  if (deck.length === 0) {
    return fullReplay(words);
  }

  return {
    perfectWordIds: cachedSet,
    deck,
    deckIndex: 0,
    roundCount: 1,
    freeReplay: false,
  };
}

/** 再開時にもう一周モードが必要か */
export function shouldResumeAsReplay(
  words: Word[],
  interrupted: InterruptedSession,
  dayStartHour: number,
): boolean {
  if (interrupted.ignorePerfectToday) return true;
  const init = createSessionInit(words, interrupted, false, dayStartHour);
  return init.freeReplay;
}
